import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {useNavigate} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Tabs,
  Tab,
  Box,
  Alert,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, EndorsementListToolbar } from '../sections/@dashboard/user';

import { EntityContext } from '../entity';
import { isEnvelopeOrBatchCancelable } from '../utils/envelopeAndBatchCancelable';
import { getEnvelopeDeleteErrorMessage } from '../utils/envelopeDeleteError';
import { looksLikeUuid } from '../utils/looksLikeUuid';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', alignRight: false },
  { id: 'company', label: 'Data de criação', alignRight: false },
  { id: 'role', label: 'Ultima alteração', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy, isBatch = false) {
  // Mapear os IDs das colunas para os campos reais dos dados
  const fieldMap = {
    'name': isBatch ? 'batch_envelope_name' : 'envelope_name',
    'company': 'created_at',
    'role': 'updated_at',
    'status': 'status',
  };
  
  const actualField = fieldMap[orderBy] || orderBy;
  let aValue = a[actualField];
  let bValue = b[actualField];
  
  // Tratar valores nulos ou indefinidos
  if (aValue == null) aValue = '';
  if (bValue == null) bValue = '';
  
  // Converter para string se necessário
  aValue = String(aValue);
  bValue = String(bValue);
  
  // Para campos de data (created_at, updated_at), comparar como datas
  if (actualField === 'created_at' || actualField === 'updated_at') {
    const aDate = new Date(aValue);
    const bDate = new Date(bValue);
    
    // Se as datas são válidas, comparar como números
    if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
      if (bDate < aDate) {
        return -1;
      }
      if (bDate > aDate) {
        return 1;
      }
      return 0;
    }
  }
  
  // Para outros campos, comparar como strings (case-insensitive)
  const aLower = aValue.toLowerCase();
  const bLower = bValue.toLowerCase();
  
  if (bLower < aLower) {
    return -1;
  }
  if (bLower > aLower) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy, isBatch = false) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, isBatch)
    : (a, b) => -descendingComparator(a, b, orderBy, isBatch);
}

function applySortFilter(array, comparator, query, fieldName = 'envelope_name') {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  const sortedArray = stabilizedThis.map((el) => el[0]);
  if (query) {
    return filter(sortedArray, (_item) => {
      const fieldValue = _item[fieldName];
      if (fieldValue == null) return false;
      const fieldValueStr = String(fieldValue);
      const queryStr = String(query);
      return fieldValueStr.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1;
    });
  }
  return sortedArray;
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function EndorsementsPage() {
  const navigate = useNavigate();

  const [envelopesList, setEnvelopesList] = useState([]);
  const [batchesList, setBatchesList] = useState([]);

  // Estados para paginação observacional dos endossos individuais
  const [loadedPages, setLoadedPages] = useState(new Set([1])); // Páginas já carregadas
  const [totalResults, setTotalResults] = useState(0); // Total real de resultados da API
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading para carregamento de mais dados

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');
  const [filterBatchName, setFilterBatchName] = useState('');
  const [appliedLookup, setAppliedLookup] = useState('');
  const [appliedBatchLookup, setAppliedBatchLookup] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [currentTab, setCurrentTab] = useState(0); // Estado para controlar a aba ativa
  const [deleteEnvelopeError, setDeleteEnvelopeError] = useState(null);

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0); // Resetar a página ao mudar de aba
    setSelected([]); // Resetar a seleção ao mudar de aba
    setDeleteEnvelopeError(null);

    // Resetar estados de paginação apenas para endossos individuais
    if (newValue === 0) {
      setLoadedPages(new Set([1]));
      setTotalResults(0);
      setIsLoadingMore(false);
    }
  };

  const {
    getEntity,
    getEndorsementsList,
    getBatchEndorsementsList,
    deleteEnvelope,
    cancelBatchEndorsement,
  } = useContext(EntityContext);

  useEffect(() => {
    getEntity()
        .then((data) => {
          
          // setUserList(data.members)
        })
    }, [])

  const navigateToNewEnvelope = () => {
    // 👇️ navigate to /contacts
    navigate('/dashboard/envelope');
  };

  useEffect(() => {
    getEnvList()
    }, [])

    const getEnvList = useCallback(async () => {
    
        getEndorsementsList(1)
            .then((data) => {
              
              if (data) {
                setEnvelopesList(data.endorsements)
                setTotalResults(data.total_results || 0)
              }
            })

        getBatchEndorsementsList()
            .then((data) => {
              
              if (data) {
                setBatchesList(data.batch_endorsements)
              }
            })
        
          }, [])

  useEffect(() => {
    if (!appliedLookup) {
      return;
    }
    getEndorsementsList(1, appliedLookup).then((data) => {
      setEnvelopesList(data?.endorsements || []);
      setTotalResults(data?.total_results || data?.endorsements?.length || 0);
      setLoadedPages(new Set([1]));
    });
  }, [appliedLookup]);

  useEffect(() => {
    if (!appliedBatchLookup) {
      return;
    }
    getBatchEndorsementsList(appliedBatchLookup).then((data) => {
      setBatchesList(data?.batch_endorsements || []);
    });
  }, [appliedBatchLookup]);

  const hadleDelete = (envelopeKey) => {
    setDeleteEnvelopeError(null);
    deleteEnvelope(envelopeKey)
      .then(() => {
        getEnvList();
      })
      .catch((error) => {
        setDeleteEnvelopeError(getEnvelopeDeleteErrorMessage(error));
      });
  };

  const handleBatchDelete = (batchKey) => {
    if (!batchKey) return;
    cancelBatchEndorsement(batchKey)
      .then(() => {
        getEnvList();
      })
      .catch(() => {});
  };

  // Função para carregar mais dados de endossos quando necessário
  const loadMoreDataIfNeeded = useCallback(async (currentPage) => {
    // Calcular qual página da API precisamos
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    // Calcular qual página da API contém o endIndex
    const apiPageNeeded = Math.ceil(endIndex / 50); // 50 é o results_per_page da API
    
    // Verificar se já carregamos essa página e se não excedemos o total
    if (!loadedPages.has(apiPageNeeded) && endIndex <= totalResults && totalResults > 0 && !appliedLookup) {
      setIsLoadingMore(true);
      
      try {
        const data = await getEndorsementsList(apiPageNeeded);
        if (data && data.endorsements) {
          // Adicionar novos dados ao array existente
          setEnvelopesList(prevList => [...prevList, ...data.endorsements]);
          setLoadedPages(prev => new Set([...prev, apiPageNeeded]));
        }
      } catch (error) {
        console.error('Erro ao carregar mais dados:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [loadedPages, rowsPerPage, totalResults, appliedLookup]);

  // Calcular arrays filtrados antes de usar nos handlers
  const filteredUsers = useMemo(() => 
    applySortFilter(envelopesList, getComparator(order, orderBy, false), appliedLookup ? '' : filterName, 'envelope_name'),
    [envelopesList, order, orderBy, filterName, appliedLookup]
  );
  
  const filteredBatches = useMemo(() => 
    applySortFilter(batchesList, getComparator(order, orderBy, true), appliedBatchLookup ? '' : filterBatchName, 'batch_envelope_name'),
    [batchesList, order, orderBy, filterBatchName, appliedBatchLookup]
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const listToUse = currentTab === 0 ? filteredUsers : filteredBatches;
      const newSelecteds = listToUse.map((n) => currentTab === 0 ? n.envelope_name : n.batch_envelope_name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Verificar se precisamos carregar mais dados (apenas para endossos individuais)
    if (currentTab === 0) {
      loadMoreDataIfNeeded(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // Verificar se precisamos carregar mais dados quando rowsPerPage mudar (apenas para endossos individuais)
  useEffect(() => {
    if (currentTab === 0 && page > 0) {
      loadMoreDataIfNeeded(page);
    }
  }, [rowsPerPage, page, currentTab]);

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleExactSearch = () => {
    const trimmed = filterName.trim();
    if (!looksLikeUuid(trimmed)) {
      return;
    }
    setPage(0);
    setAppliedLookup(trimmed);
  };

  const handleClearSearch = () => {
    setFilterName('');
    setAppliedLookup('');
    setPage(0);
    setLoadedPages(new Set([1]));
    getEndorsementsList(1).then((data) => {
      if (data && data.endorsements) {
        setEnvelopesList(data.endorsements);
        setTotalResults(data.total_results || 0);
      }
    });
  };

  const handleFilterByBatchName = (event) => {
    setPage(0);
    setFilterBatchName(event.target.value);
  };

  const handleExactBatchSearch = () => {
    const trimmed = filterBatchName.trim();
    if (!looksLikeUuid(trimmed)) {
      return;
    }
    setPage(0);
    setAppliedBatchLookup(trimmed);
  };

  const handleClearBatchSearch = () => {
    setFilterBatchName('');
    setAppliedBatchLookup('');
    setPage(0);
    getBatchEndorsementsList().then((data) => {
      if (data && data.batch_endorsements) {
        setBatchesList(data.batch_endorsements);
      }
    });
  };

  const handleEdit = (envelopeKey) => {
    navigate(`/dashboard/envelope/${envelopeKey}`);
  }
  
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (currentTab === 0 ? filteredUsers.length : filteredBatches.length)) : 0;

  const isNotFound = !filteredUsers.length && !!(appliedLookup || filterName);
  const isNotFoundBatches = !filteredBatches.length && !!(appliedBatchLookup || filterBatchName);

  return (
    <>
      <Helmet>
        <title> Endossos </title>
      </Helmet>

      <Container>
        {deleteEnvelopeError && currentTab === 0 && (
          <Alert severity="error" onClose={() => setDeleteEnvelopeError(null)} sx={{ mb: 2 }}>
            {deleteEnvelopeError}
          </Alert>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Endossos
          </Typography>
          {/* <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={navigateToNewEnvelope}>
            Novo endosso
          </Button> */}
        </Stack>

        <Card>

        <Tabs value={currentTab} onChange={handleChangeTab} aria-label="basic tabs example">
            <Tab label="Endossos" {...a11yProps(0)} />
            <Tab label="Lotes" {...a11yProps(1)} />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
            <EndorsementListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              onExactSearch={handleExactSearch}
              onClearSearch={handleClearSearch}
              hasExactSearch={Boolean(appliedLookup)}
              searchPlaceholder="UUID do endosso"
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredUsers.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    showCheckbox={false}
                  />
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      return (
                        <TableRow hover key={row.id} tabIndex={-1}>
                          <TableCell component="th" scope="row" padding="normal">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                              {row.envelope_name}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">{row.created_at.substring(0, 10).concat(' ').concat(row.created_at.substring(11, 19))}</TableCell>

                          <TableCell align="left">{row.updated_at ? row.updated_at.substring(0, 10).concat(' ').concat(row.updated_at.substring(11, 19)) : ''}</TableCell>

                          <TableCell align="left">
                            <Label>{row.status}</Label>
                          </TableCell>

                          <TableCell align="right">
                            <IconButton size="large" color="inherit" onClick={_ => handleEdit(row.envelope_key)}>
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                            {isEnvelopeOrBatchCancelable(row.status) ? (
                              <IconButton
                                size="large"
                                color="error"
                                onClick={(_) =>
                                  hadleDelete(row.envelope_key)
                                }
                              >
                                <Iconify icon={'eva:trash-2-outline'} />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={5} />
                      </TableRow>
                    )}
                  </TableBody>

                  {isNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                          <Paper
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h6" paragraph>
                              Not found
                            </Typography>

                            <Typography variant="body2">
                              No results found for &nbsp;
                              <strong>&quot;{filterName}&quot;</strong>.
                              <br /> Try checking for typos or using complete words.
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            {currentTab === 0 && isLoadingMore && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Carregando mais dados...
                </Typography>
              </div>
            )}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={currentTab === 0 ? filteredUsers.length : filteredBatches.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
                          <EndorsementListToolbar
          numSelected={selected.length}
          filterName={filterBatchName}
          onFilterName={handleFilterByBatchName}
          onExactSearch={handleExactBatchSearch}
          onClearSearch={handleClearBatchSearch}
          hasExactSearch={Boolean(appliedBatchLookup)}
          searchPlaceholder="UUID do lote"
        />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredBatches.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    showCheckbox={false}
                  />
                  <TableBody>
                    {filteredBatches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      return (
                        <TableRow hover key={row.id} tabIndex={-1}>
                          <TableCell component="th" scope="row" padding="normal">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                              {row.batch_envelope_name}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">{row.created_at.substring(0, 10).concat(' ').concat(row.created_at.substring(11, 19))}</TableCell>

                          <TableCell align="left">{row.updated_at ? row.updated_at.substring(0, 10).concat(' ').concat(row.updated_at.substring(11, 19)) : ''}</TableCell>

                          <TableCell align="left">
                            <Label>{row.status}</Label>
                          </TableCell>

                          <TableCell align="right">
                            <IconButton size="large" color="primary" onClick={_ => navigate(`/dashboard/batch-endorsement/${row.batch_envelope_key}`)}>
                              <Iconify icon={'eva:eye-fill'} />
                            </IconButton>
                            {isEnvelopeOrBatchCancelable(row.status) ? (
                              <IconButton
                                size="large"
                                color="error"
                                onClick={(_) =>
                                  handleBatchDelete(
                                    row.batch_endorsement_key || row.batch_envelope_key
                                  )
                                }
                              >
                                <Iconify icon={'eva:trash-2-outline'} />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={5} />
                      </TableRow>
                    )}
                  </TableBody>

                  {isNotFoundBatches && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                          <Paper
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h6" paragraph>
                              Not found
                            </Typography>

                            <Typography variant="body2">
                              No results found for &nbsp;
                              <strong>&quot;{filterBatchName}&quot;</strong>.
                              <br /> Try checking for typos or using complete words.
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredBatches.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </TabPanel>


        </Card>
      </Container>
    </>
  );
}
